import React, { useState } from 'react';
import { Box, VStack, Flex, Text, Button } from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { ff } from 'theme';
import Confetti from './Confetti';

export interface ConfirmedReminder {
  title: string;
  body: string;
}

interface CollabConfirmedScreenProps {
  brandName: string;
  brandLogoUrl?: string;
  creatorAvatarUrl?: string;
  /** What the creator's side of the connector is called — "Your Visit", "Your Picks". Kept for callers. */
  connectorLabel: string;
  /** Big display title (board BookVisit success: "You're booked"). */
  title?: string;
  /** One line under the heading, describing what was just confirmed. */
  subtext: string;
  /** The summary of what was confirmed: the visit card, the chosen products. */
  children: React.ReactNode;
  reminders?: ConfirmedReminder[];
  onViewCollab: () => void;
  onHome: () => void;
}

/**
 * The moment after a creator confirms — celebration, who they just teamed up with, what they
 * confirmed, and where to go next.
 *
 * Shared by the in-store scheduling flow and the product-selection wizard. Only the summary in the
 * middle and the reminders differ between them, so those are the slots; everything else is fixed
 * so the two accepts read as one product rather than two lookalikes that drift apart.
 */
const CollabConfirmedScreen: React.FC<CollabConfirmedScreenProps> = ({
  title = "You're all set",
  subtext,
  children,
  reminders,
  onViewCollab,
  onHome,
}) => {
  // Bump to replay the confetti (fires once on mount, again on tapping the tick).
  const [burstId, setBurstId] = useState(0);
  return (
    // Ink hero band with a lime tick, then the details on white. Fixed so it fills the viewport whatever wraps it.
    <Box position="fixed" inset={0} zIndex={20} bg="white" overflowY="auto" fontFamily="display" color={ff.ink}>
      <Confetti key={burstId} />
      <Box bg={ff.ink} color="white">
        <VStack spacing="22px" align="stretch" maxW={{ base: '560px', lg: '720px' }} mx="auto" p="72px 20px 32px 20px">
          <Flex
            as="button"
            type="button"
            aria-label="Celebrate"
            onClick={() => setBurstId((n) => n + 1)}
            w="64px"
            h="64px"
            borderRadius="999px"
            bg={ff.lime}
            color={ff.ink}
            align="center"
            justify="center"
            _active={{ transform: 'scale(0.92)' }}
            transition="transform 0.1s"
          >
            <FiCheck size={30} strokeWidth={2.8} />
          </Flex>
          <Text as="h1" m={0} textStyle="display" fontSize="35px" lineHeight={0.86}>
            {title}
          </Text>
          <Text m={0} fontSize="17px" lineHeight="1.4" color={ff.faint}>
            {subtext}
          </Text>
        </VStack>
      </Box>
      <VStack
        spacing="22px"
        align="stretch"
        maxW={{ base: '560px', lg: '720px' }}
        mx="auto"
        p="22px 20px 40px 20px"
        pb="calc(40px + env(safe-area-inset-bottom))"
      >
        {/* What was confirmed */}
        <Box borderRadius="20px" bg="white" border="1px solid" borderColor={ff.border} overflow="hidden">
          {children}
        </Box>

        <Box p="6px 18px" borderRadius="20px" bg="white" border="1px solid" borderColor={ff.border}>
          <Flex direction="column" gap="3px" py="12px" borderBottom={reminders?.length ? `1px solid ${ff.line}` : undefined}>
            <Text fontSize="15px" fontWeight={600} lineHeight="normal">
              Please review campaign details &amp; instructions carefully.
            </Text>
            <Text fontSize="13px" color={ff.text2} lineHeight="1.4">
              All the information you need to create great content is inside!
            </Text>
          </Flex>
          {reminders?.map((r, i) => (
            <Flex key={i} gap="12px" py="12px" align="flex-start" borderBottom={i === reminders.length - 1 ? undefined : `1px solid ${ff.line}`}>
              <Flex flex="none" w="24px" h="24px" borderRadius="999px" bg={ff.ink} color={ff.lime} align="center" justify="center" fontSize="12px" fontWeight={600}>
                {i + 1}
              </Flex>
              <Flex direction="column" gap="2px" minW={0}>
                <Text fontSize="15px" fontWeight={600} lineHeight="normal">{r.title}</Text>
                <Text fontSize="13px" color={ff.text2} lineHeight="1.4">{r.body}</Text>
              </Flex>
            </Flex>
          ))}
        </Box>

        <VStack spacing="8px" align="stretch">
          <Button
            h="56px"
            borderRadius="999px"
            bg={ff.ink}
            color={ff.lime}
            fontFamily="display"
            fontSize="16px"
            fontWeight={600}
            _hover={{ bg: ff.ink }}
            _active={{ bg: '#2A2A2C' }}
            onClick={onViewCollab}
          >
            View progress
          </Button>
          <Text fontSize="13px" textAlign="center" color={ff.text2}>You'll receive a confirmation email shortly.</Text>
          <Button
            variant="unstyled"
            alignSelf="center"
            h="44px"
            px="12px"
            fontFamily="display"
            fontSize="15px"
            fontWeight={600}
            color={ff.ink}
            onClick={onHome}
          >
            Home
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
};

export default CollabConfirmedScreen;
